import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  HeartIcon,
  EyeIcon,
  ClockIcon,
  UserIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChatBubbleLeftEllipsisIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('preview');

  useEffect(() => {
    // Simulate fetching document data
    const fetchDocument = () => {
      const mockDocument = {
        id: parseInt(id),
        title: 'Employee Handbook 2025',
        fileName: 'employee-handbook-2025.pdf',
        type: 'PDF',
        category: 'HR Policies',
        size: '2.4 MB',
        uploadDate: '2025-01-15',
        lastModified: '2025-01-20',
        version: '1.2',
        author: 'Dr. Sarah Johnson',
        authorAvatar: '/images/avatars/sarah-johnson.jpg',
        department: 'Human Resources',
        description: 'Comprehensive employee handbook covering policies, procedures, benefits, and guidelines for all Apollo Healthcare staff members.',
        tags: ['HR', 'Policies', 'Guidelines', 'Benefits', 'Procedures'],
        downloads: 145,
        views: 289,
        likes: 23,
        isPublic: true,
        permissions: {
          canEdit: true,
          canDelete: true,
          canShare: true,
          canComment: true
        },
        content: `
          <div class="document-preview">
            <h1>Apollo Healthcare Employee Handbook</h1>
            <h2>Version 1.2 - January 2025</h2>
            
            <h3>Table of Contents</h3>
            <ul>
              <li>1. Welcome to Apollo Healthcare</li>
              <li>2. Company Mission and Values</li>
              <li>3. Employment Policies</li>
              <li>4. Benefits and Compensation</li>
              <li>5. Professional Development</li>
              <li>6. Safety and Security</li>
              <li>7. Code of Conduct</li>
              <li>8. Contact Information</li>
            </ul>
            
            <h3>1. Welcome to Apollo Healthcare</h3>
            <p>Welcome to Apollo Healthcare! We are excited to have you join our team of dedicated healthcare professionals. This handbook will guide you through our policies, procedures, and the resources available to support your success.</p>
            
            <h3>2. Company Mission and Values</h3>
            <p>Our mission is to provide exceptional healthcare services while maintaining the highest standards of patient care, safety, and professional excellence.</p>
            
            <h4>Core Values:</h4>
            <ul>
              <li><strong>Compassion:</strong> We treat every patient with empathy and understanding</li>
              <li><strong>Excellence:</strong> We strive for the highest quality in everything we do</li>
              <li><strong>Innovation:</strong> We embrace new technologies and methods to improve care</li>
              <li><strong>Integrity:</strong> We act with honesty and transparency in all our interactions</li>
              <li><strong>Teamwork:</strong> We collaborate effectively to achieve our goals</li>
            </ul>
          </div>
        `,
        relatedDocuments: [
          { id: 2, title: 'IT Security Policy', type: 'PDF', uploadDate: '2025-01-10' },
          { id: 3, title: 'Emergency Procedures', type: 'DOC', uploadDate: '2025-01-12' },
          { id: 4, title: 'Benefits Overview', type: 'PDF', uploadDate: '2025-01-08' }
        ]
      };

      const mockComments = [
        {
          id: 1,
          author: 'Dr. Michael Chen',
          avatar: '/images/avatars/michael-chen.jpg',
          date: '2025-01-22',
          content: 'Great update to the handbook! The new benefits section is very comprehensive.',
          replies: []
        },
        {
          id: 2,
          author: 'Lisa Rodriguez',
          avatar: '/images/avatars/lisa-rodriguez.jpg',
          date: '2025-01-21',
          content: 'Could we add more information about the professional development programs?',
          replies: [
            {
              id: 3,
              author: 'Dr. Sarah Johnson',
              avatar: '/images/avatars/sarah-johnson.jpg',
              date: '2025-01-22',
              content: 'Good suggestion! I\'ll include that in the next version.'
            }
          ]
        }
      ];

      setDocument(mockDocument);
      setComments(mockComments);
      setIsLiked(Math.random() > 0.7);
      setIsBookmarked(Math.random() > 0.6);
      setLoading(false);
    };

    setTimeout(fetchDocument, 500);
  }, [id]);

  const handleLike = () => {
    setIsLiked(!isLiked);
    // Update like count in document
    setDocument(prev => ({
      ...prev,
      likes: isLiked ? prev.likes - 1 : prev.likes + 1
    }));
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleDownload = () => {
    // Simulate download
    console.log('Downloading document:', document.fileName);
    // Update download count
    setDocument(prev => ({
      ...prev,
      downloads: prev.downloads + 1
    }));
  };

  const handleShare = () => {
    // Simulate sharing
    navigator.clipboard.writeText(window.location.href);
    alert('Document link copied to clipboard!');
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      console.log('Deleting document:', document.id);
      navigate('/documents');
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      const comment = {
        id: comments.length + 1,
        author: 'Current User',
        avatar: '/images/avatars/current-user.jpg',
        date: new Date().toISOString().split('T')[0],
        content: newComment,
        replies: []
      };
      setComments([...comments, comment]);
      setNewComment('');
    }
  };

  const tabs = [
    { id: 'preview', name: 'Preview', icon: EyeIcon },
    { id: 'details', name: 'Details', icon: DocumentIcon },
    { id: 'comments', name: 'Comments', icon: ChatBubbleLeftEllipsisIcon, count: comments.length },
    { id: 'versions', name: 'Versions', icon: ClockIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Document not found</h3>
          <p className="mt-1 text-sm text-gray-500">The document you're looking for doesn't exist.</p>
          <div className="mt-6">
            <Link
              to="/documents"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to Documents
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Custom Header for Document Details */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Breadcrumb */}
            <nav className="flex mb-4" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link to="/documents" className="text-gray-700 hover:text-gray-900">
                    Documents
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <span className="mx-2 text-gray-400">/</span>
                    <Link to={`/documents/categories/${document.category.toLowerCase().replace(' ', '-')}`} className="text-gray-700 hover:text-gray-900">
                      {document.category}
                    </Link>
                  </div>
                </li>
                <li>
                  <div className="flex items-center">
                    <span className="mx-2 text-gray-400">/</span>
                    <span className="text-gray-500">{document.title}</span>
                  </div>
                </li>
              </ol>
            </nav>

            {/* Document Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <DocumentIcon className="flex-shrink-0 h-8 w-8 text-gray-400" />
                  <div className="ml-3">
                    <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl sm:truncate">
                      {document.title}
                    </h1>
                    <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        {document.author}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        {document.category}
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                        Updated {document.lastModified}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-5 flex lg:mt-0 lg:ml-4">
                <span className="hidden sm:block">
                  <button
                    onClick={handleBookmark}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {isBookmarked ? (
                      <BookmarkIconSolid className="-ml-1 mr-2 h-4 w-4 text-yellow-500" />
                    ) : (
                      <BookmarkIcon className="-ml-1 mr-2 h-4 w-4" />
                    )}
                    {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                  </button>
                </span>

                <span className="ml-3 hidden sm:block">
                  <button
                    onClick={handleLike}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {isLiked ? (
                      <HeartIconSolid className="-ml-1 mr-2 h-4 w-4 text-red-500" />
                    ) : (
                      <HeartIcon className="-ml-1 mr-2 h-4 w-4" />
                    )}
                    {document.likes}
                  </button>
                </span>

                <span className="ml-3 hidden sm:block">
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <ShareIcon className="-ml-1 mr-2 h-4 w-4" />
                    Share
                  </button>
                </span>

                <span className="ml-3">
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
                    Download
                  </button>
                </span>

                {document.permissions.canEdit && (
                  <span className="ml-3">
                    <div className="relative inline-block text-left">
                      <div className="flex">
                        <button className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-l-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button className="inline-flex items-center px-3 py-2 border-l-0 border border-gray-300 rounded-r-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                          <TrashIcon className="h-4 w-4 text-red-500" onClick={handleDelete} />
                        </button>
                      </div>
                    </div>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <div className="px-6">
                  <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                      >
                        <tab.icon className="mr-2 h-4 w-4" />
                        {tab.name}
                        {tab.count !== undefined && (
                          <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              <div className="p-6">
                {/* Preview Tab */}
                {activeTab === 'preview' && (
                  <div className="prose max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: document.content }} />
                  </div>
                )}

                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Document Information</h3>
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">File Name</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.fileName}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">File Type</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.type}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">File Size</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.size}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Version</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.version}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Upload Date</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.uploadDate}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Last Modified</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.lastModified}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Department</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.department}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Downloads</dt>
                          <dd className="mt-1 text-sm text-gray-900">{document.downloads}</dd>
                        </div>
                      </dl>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
                      <p className="text-sm text-gray-900">{document.description}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {document.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments Tab */}
                {activeTab === 'comments' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Comments</h3>
                      
                      {/* Add Comment Form */}
                      <form onSubmit={handleAddComment} className="mb-6">
                        <div>
                          <label htmlFor="comment" className="sr-only">
                            Add a comment
                          </label>
                          <textarea
                            id="comment"
                            name="comment"
                            rows={3}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                          />
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button
                            type="submit"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Post Comment
                          </button>
                        </div>
                      </form>

                      {/* Comments List */}
                      <div className="space-y-6">
                        {comments.map((comment) => (
                          <div key={comment.id} className="flex space-x-3">
                            <div className="flex-shrink-0">
                              <img
                                className="h-8 w-8 rounded-full"
                                src={comment.avatar}
                                alt={comment.author}
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author)}&background=3B82F6&color=fff`;
                                }}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div>
                                <div className="text-sm">
                                  <span className="font-medium text-gray-900">{comment.author}</span>
                                </div>
                                <div className="text-sm text-gray-500">
                                  <time dateTime={comment.date}>{comment.date}</time>
                                </div>
                              </div>
                              <div className="mt-2 text-sm text-gray-700">
                                <p>{comment.content}</p>
                              </div>
                              
                              {/* Replies */}
                              {comment.replies && comment.replies.length > 0 && (
                                <div className="mt-4 ml-6 space-y-4">
                                  {comment.replies.map((reply) => (
                                    <div key={reply.id} className="flex space-x-3">
                                      <div className="flex-shrink-0">
                                        <img
                                          className="h-6 w-6 rounded-full"
                                          src={reply.avatar}
                                          alt={reply.author}
                                          onError={(e) => {
                                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(reply.author)}&background=3B82F6&color=fff`;
                                          }}
                                        />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div>
                                          <div className="text-sm">
                                            <span className="font-medium text-gray-900">{reply.author}</span>
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            <time dateTime={reply.date}>{reply.date}</time>
                                          </div>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-700">
                                          <p>{reply.content}</p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Versions Tab */}
                {activeTab === 'versions' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Version History</h3>
                    <div className="space-y-3">
                      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">CURRENT</span>
                            <span className="ml-3 text-sm font-medium text-gray-900">Version 1.2</span>
                          </div>
                          <div className="text-sm text-gray-500">January 20, 2025</div>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">Updated benefits section and added new safety protocols</p>
                        <div className="mt-2 text-sm text-gray-500">By Dr. Sarah Johnson</div>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">Version 1.1</span>
                          </div>
                          <div className="text-sm text-gray-500">December 15, 2024</div>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">Minor updates to HR policies and contact information</p>
                        <div className="mt-2 text-sm text-gray-500">By Dr. Sarah Johnson</div>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">Version 1.0</span>
                          </div>
                          <div className="text-sm text-gray-500">November 1, 2024</div>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">Initial version of the employee handbook</p>
                        <div className="mt-2 text-sm text-gray-500">By Dr. Sarah Johnson</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Document Stats */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Document Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <EyeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Views</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{document.views}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ArrowDownTrayIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Downloads</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{document.downloads}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <HeartIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Likes</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{document.likes}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Comments</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{comments.length}</span>
                  </div>
                </div>
              </div>

              {/* Related Documents */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Related Documents</h3>
                <div className="space-y-3">
                  {document.relatedDocuments.map((relatedDoc) => (
                    <Link
                      key={relatedDoc.id}
                      to={`/documents/${relatedDoc.id}`}
                      className="block hover:bg-gray-50 p-2 rounded-md"
                    >
                      <div className="flex items-center">
                        <DocumentIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {relatedDoc.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {relatedDoc.type} • {relatedDoc.uploadDate}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={handleDownload}
                    className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                  >
                    <ShareIcon className="h-4 w-4 mr-2" />
                    Share Document
                  </button>
                  <button className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md">
                    <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                    Create Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DocumentDetail;
